import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dayCheckbox", "startHour", "endHour", "output", "preview", "previewText"]

  // Nomes dos dias da semana em português
  DIAS_SEMANA = [
    "Domingo",
    "Segunda-feira",
    "Terça-feira",
    "Quarta-feira",
    "Quinta-feira",
    "Sexta-feira",
    "Sábado"
  ]

  connect() {
    // Verifica se existe valor (modo edição)
    const existingValue = this.outputTarget.value

    if (existingValue) {
      // Modo edição: mostra preview do valor existente
      this.showPreview(existingValue)
      // Tenta fazer parse do valor existente
      this.parseExistingValue()
    }

    // Se conseguiu parsear, atualiza o output
    // Se não, mantém o valor existente
    if (this.getSelectedDays().length > 0 || !existingValue) {
      this.updateOutput()
    }
  }

  updateOutput() {
    const selectedDays = this.getSelectedDays()
    const startHour = parseInt(this.startHourTarget.value)
    const endHour = parseInt(this.endHourTarget.value)

    // Valida se há dias selecionados
    if (selectedDays.length === 0) {
      // Não limpa o campo se já tinha valor (modo edição)
      const existingValue = this.outputTarget.value
      if (!existingValue) {
        this.hidePreview()
      }
      return
    }

    // Valida horários
    if (!this.validateHours(startHour, endHour)) {
      // Não limpa o campo se já tinha valor (modo edição)
      const existingValue = this.outputTarget.value
      if (!existingValue) {
        this.hidePreview()
      }
      return
    }

    // Gera a string formatada
    const formatted = this.formatFullString(selectedDays, startHour, endHour)
    this.outputTarget.value = formatted
    this.showPreview(formatted)
  }

  getSelectedDays() {
    // Retorna array de índices dos dias selecionados
    return this.dayCheckboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => parseInt(checkbox.dataset.funcionamentoDayParam))
      .sort((a, b) => a - b)
  }

  groupConsecutiveDays(selectedDays) {
    if (selectedDays.length === 0) return []

    const groups = []
    let currentGroup = { start: selectedDays[0], end: selectedDays[0] }

    for (let i = 1; i < selectedDays.length; i++) {
      if (selectedDays[i] === currentGroup.end + 1) {
        // Dia consecutivo - estende o grupo atual
        currentGroup.end = selectedDays[i]
      } else {
        // Gap - salva grupo atual e inicia novo
        groups.push(currentGroup)
        currentGroup = { start: selectedDays[i], end: selectedDays[i] }
      }
    }

    groups.push(currentGroup)
    return groups
  }

  formatDayRange(start, end) {
    if (start === end) {
      // Dia único
      return this.DIAS_SEMANA[start]
    } else {
      // Range de dias
      // Remove "-feira" do dia inicial para ranges
      const startDay = this.DIAS_SEMANA[start].replace('-feira', '')
      return `${startDay} à ${this.DIAS_SEMANA[end]}`
    }
  }

  formatFullString(selectedDays, startHour, endHour) {
    // Caso especial: todos os dias
    if (selectedDays.length === 7) {
      return `Todos os dias das ${startHour}h às ${endHour}h`
    }

    // Agrupa dias consecutivos
    const groups = this.groupConsecutiveDays(selectedDays)
    const dayParts = groups.map(g => this.formatDayRange(g.start, g.end))

    // Junta os grupos com vírgula e "e"
    let dayString
    if (dayParts.length === 1) {
      dayString = dayParts[0]
    } else if (dayParts.length === 2) {
      dayString = `${dayParts[0]} e ${dayParts[1]}`
    } else {
      const last = dayParts.pop()
      dayString = `${dayParts.join(', ')} e ${last}`
    }

    return `${dayString} das ${startHour}h às ${endHour}h`
  }

  validateHours(start, end) {
    // Valida se os horários são válidos
    return !isNaN(start) &&
           !isNaN(end) &&
           start >= 0 &&
           start <= 23 &&
           end >= 0 &&
           end <= 23 &&
           start < end
  }

  parseExistingValue() {
    const value = this.outputTarget.value
    if (!value) return

    // Extrai horários usando regex
    const hourMatch = value.match(/das (\d{1,2})h às (\d{1,2})h/)
    if (hourMatch) {
      this.startHourTarget.value = hourMatch[1]
      this.endHourTarget.value = hourMatch[2]
    }

    // Tenta identificar dias selecionados
    const daysToCheck = []

    // Caso especial: Todos os dias
    if (value.includes("Todos os dias")) {
      daysToCheck.push(0, 1, 2, 3, 4, 5, 6)
    } else {
      // Mapeia nomes para índices
      const dayMap = {
        "Domingo": 0,
        "Segunda": 1, "Segunda-feira": 1,
        "Terça": 2, "Terça-feira": 2,
        "Quarta": 3, "Quarta-feira": 3,
        "Quinta": 4, "Quinta-feira": 4,
        "Sexta": 5, "Sexta-feira": 5,
        "Sábado": 6
      }

      // Procura por TODOS os ranges do tipo "Dia à Dia"
      const rangeRegex = /(Domingo|Segunda|Terça|Quarta|Quinta|Sexta|Sábado) à (Domingo|Segunda-feira|Terça-feira|Quarta-feira|Quinta-feira|Sexta-feira|Sábado)/g
      let rangeMatch

      while ((rangeMatch = rangeRegex.exec(value)) !== null) {
        const startDayName = rangeMatch[1]
        const endDayName = rangeMatch[2]
        const startDay = dayMap[startDayName]
        const endDay = dayMap[endDayName]

        // Adiciona todos os dias do range
        if (startDay !== undefined && endDay !== undefined) {
          if (startDay <= endDay) {
            // Range normal (ex: Segunda à Sexta)
            for (let i = startDay; i <= endDay; i++) {
              if (!daysToCheck.includes(i)) daysToCheck.push(i)
            }
          } else {
            // Range que passa pela semana (ex: Sábado à Segunda)
            for (let i = startDay; i <= 6; i++) {
              if (!daysToCheck.includes(i)) daysToCheck.push(i)
            }
            for (let i = 0; i <= endDay; i++) {
              if (!daysToCheck.includes(i)) daysToCheck.push(i)
            }
          }
        }
      }

      // Agora procura por dias individuais que NÃO fazem parte de um range
      // Remove os ranges da string para procurar apenas dias soltos
      let valueWithoutRanges = value.replace(rangeRegex, '')

      // Procura cada dia individual no texto restante
      Object.entries(dayMap).forEach(([dayName, dayIndex]) => {
        // Evita contar dias que já estão em ranges usando lookahead/lookbehind negativo
        const dayRegex = new RegExp(`\\b${dayName}(?!-feira à| à)`, 'i')
        if (dayRegex.test(valueWithoutRanges) && !daysToCheck.includes(dayIndex)) {
          daysToCheck.push(dayIndex)
        }
      })
    }

    // Marca os checkboxes identificados
    if (daysToCheck.length > 0) {
      this.dayCheckboxTargets.forEach(checkbox => {
        const day = parseInt(checkbox.dataset.funcionamentoDayParam)
        checkbox.checked = daysToCheck.includes(day)
      })
    }
  }

  showPreview(text) {
    if (this.hasPreviewTarget && this.hasPreviewTextTarget) {
      this.previewTextTarget.textContent = text
      this.previewTarget.style.display = "block"
    }
  }

  hidePreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.style.display = "none"
    }
  }
}
